import React, { useState, useRef, KeyboardEvent } from 'react';
import { Card, CardHeader } from 'reactstrap';
import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react';

interface CollapsibleCardProps {
  title: React.ReactNode;
  defaultCollapsed?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

export default function CollapsibleCard({
  title,
  defaultCollapsed = true,
  className,
  style,
  children,
}: CollapsibleCardProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const contentId = useRef(`collapsible-${Math.random().toString(36).slice(2)}`).current;

  const toggle = () => setCollapsed((prev) => !prev);

  const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <Card className={className} style={style}>
      <CardHeader
        className="d-flex align-items-center justify-content-between"
        style={{ cursor: 'pointer', userSelect: 'none' }}
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        aria-controls={contentId}
        onClick={toggle}
        onKeyDown={handleKeyDown}
      >
        <strong>{title}</strong>
        <span className="text-muted d-flex">
          {collapsed ? <ChevronsUpDown size={16} /> : <ChevronsDownUp size={16} />}
        </span>
      </CardHeader>
      <div
        id={contentId}
        aria-hidden={collapsed}
        style={{
          display: 'grid',
          gridTemplateRows: collapsed ? '0fr' : '1fr',
          transition: 'grid-template-rows 0.3s ease',
        }}
      >
        <div style={{ overflow: 'hidden' }}>
          {children}
        </div>
      </div>
    </Card>
  );
}
